import { createContext, useState, useEffect, useMemo } from "react";
import { getBrands, getProductsByBrand, getProductById, getProductSearch, getKitMaintenance } from '../service/ApiLeiten';
import { getCurrencyNames } from '../service/ApiAdmin';
import { getProductsWithSolutions } from '../service/ApiServicioTecnico';
import { useApiLeiten } from "../hooks/useApiLeiten";

export const ApiContext = createContext();

export const ApiData = ({ children }) => {
    // Arranca en true: al montar ya se disparan las cargas iniciales.
    const [loading, setLoading] = useState(true);

    const brands = useApiLeiten({ apiService: getBrands });
    const productsByBrand = useApiLeiten({ apiService: getProductsByBrand });
    const productById = useApiLeiten({ apiService: getProductById });
    const productSearch = useApiLeiten({ apiService: getProductSearch });

    // Estos dos son iguales para toda la app: se piden una sola vez acá en lugar
    // de una vez por card (eran 4 requests de cotización por producto abierto).
    const kitMaintenance = useApiLeiten({ apiService: getKitMaintenance });
    const currency = useApiLeiten({ apiService: getCurrencyNames });
    const productsWithSolutions = useApiLeiten({ apiService: getProductsWithSolutions });

    useEffect(() => {
        let cancelled = false;

        // Promise.all y no un .finally por llamada: antes, la primera que terminaba
        // apagaba el flag y las otras seguían cargando con loading ya en false.
        Promise.all([
            brands.fetch(),
            productSearch.fetch(),
            kitMaintenance.fetch(),
            currency.fetch(),
            productsWithSolutions.fetch(),
        ]).finally(() => {
            if (!cancelled) setLoading(false);
        });

        return () => { cancelled = true; };
    }, []); //eslint-disable-line

    // Los ids de kit se consultan por pertenencia, así que van como Set de strings.
    const kitMaintenanceIds = useMemo(() => {
        if (!Array.isArray(kitMaintenance.data)) return null;
        return new Set(kitMaintenance.data.map(item => String(item.id)));
    }, [kitMaintenance.data]);

    // Set de ids (como string) de los equipos con reparaciones cargadas.
    // null cuando todavía no llegó o cuando el endpoint falló: en ese caso no se
    // filtra nada. Es a propósito: si el servicio técnico no responde, es mejor
    // mostrar todo el catálogo que mostrarlo vacío.
    const serviceableProductIds = useMemo(() => {
        if (!Array.isArray(productsWithSolutions.data)) return null;
        return new Set(productsWithSolutions.data.map(id => String(id)));
    }, [productsWithSolutions.data]);

    // getCurrencyNames devuelve [dolar, euro] en ese orden.
    const rates = useMemo(() => {
        const values = currency.data;
        if (!Array.isArray(values) || values.length < 2) return { dolar: null, euro: null };
        return { dolar: values[0].value, euro: values[1].value };
    }, [currency.data]);

    return (
        <ApiContext.Provider value={{
            // marcas
            brands: brands.data,
            brandsError: brands.error,
            fetchBrands: brands.fetch,

            // productos de una marca
            productsByBrand: productsByBrand.data,
            productsByBrandError: productsByBrand.error,
            productsByBrandLoading: productsByBrand.loading,
            fetchProductsByBrand: productsByBrand.fetch,
            emptyProductByBrand: productsByBrand.empty,

            // producto seleccionado
            productById: productById.data,
            productByIdError: productById.error,
            productByIdLoading: productById.loading,
            fetchProductById: productById.fetch,
            emptyProductById: productById.empty,

            // índice para el buscador
            productSearch: productSearch.data,
            productSearchError: productSearch.error,
            fetchProductSearch: productSearch.fetch,

            // datos compartidos por toda la app
            kitMaintenance: kitMaintenance.data,
            kitMaintenanceIds,
            rates,
            serviceableProductIds,

            // carga inicial de la app
            loading,
        }}>
            {children}
        </ApiContext.Provider>
    );
};
